import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import { connectionRows, type EntityRefValue, IssueViewDataDocument } from "#app/lib/data";
import type {
  IssueViewStage,
  IssueTypeWithFields,
  CustomFieldDef,
} from "#app/components/issue-view/issue-view-types";
import type { AccessLevel } from "#app/lib/utils/access-level";
import type { Omittable } from "@salestrak/ui";

export function useIssueViewData(issueId: string | undefined) {
  const variables = useMemo(() => ({ issueId: issueId! }), [issueId]);
  const { data, loading, error, refetch } = useQuery(IssueViewDataDocument, {
    variables,
    skip: !issueId,
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
  });

  const rawIssue = data?.issue as
    | (Record<string, unknown> & Omittable<{
        stage?: IssueViewStage | null;
        assignees?: EntityRefValue[];
        values?: Record<string, unknown> | null;
        mediaCount?: number | null;
        workflowId?: string | null;
        addressId?: string | null;
        viewerAccessLevel?: AccessLevel | null;
        address?: { id: string; addressId: string; addressLine1?: string | null; city?: string | null; stateProvince?: string | null; postalCode?: string | null; wholeAddress?: string | null; latLng?: { lat: number; lng: number } | null } | null;
      }>)
    | null
    | undefined;

  const issue = rawIssue ?? null;
  const stage = rawIssue?.stage ?? null;
  const assignees = rawIssue?.assignees ?? [];
  const values = rawIssue?.values ?? null;
  const mediaCount = rawIssue?.mediaCount ?? null;
  const workflowId = rawIssue?.workflowId ?? null;
  const viewerAccessLevel = rawIssue?.viewerAccessLevel ?? null;
  const address = rawIssue ? (rawIssue.address ?? null) : undefined;

  const issueTypes = useMemo<IssueTypeWithFields[]>(
    () => connectionRows(data?.issueTypes),
    [data?.issueTypes],
  );

  const customFields = useMemo<CustomFieldDef[]>(
    () => connectionRows(data?.customFields),
    [data?.customFields],
  );

  return { issue, stage, assignees, values, mediaCount, workflowId, viewerAccessLevel, address, issueTypes, customFields, loading, error, refetch };
}
